"use client";

import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import Icon, {ICON_TYPE} from "@/components/library/Icon";
import type {GailGptDictationStatus} from "@/hooks/useGailGptDictation";

type DictationButtonProps = Readonly<{
   status: GailGptDictationStatus;
   onClick: () => void;
   disabled?: boolean;
}>;

function dictationButtonLabel(
   status: GailGptDictationStatus,
   t: (key: string) => string
): string {
   switch (status) {
      case "recording":
         return t("dictation.stop");
      case "transcribing":
         return t("dictation.transcribing");
      case "idle":
      case "unsupported":
         return t("dictation.start");
      default: {
         const _exhaustive: never = status;
         return _exhaustive;
      }
   }
}

export default function DictationButton({status, onClick, disabled = false}: DictationButtonProps) {
   const {t} = useTranslation("ask");
   const isRecording = status === "recording";
   const isTranscribing = status === "transcribing";
   const label = dictationButtonLabel(status, t);

   return (
      <Tooltip delayDuration={0}>
         <TooltipTrigger asChild>
            <button
               type="button"
               onClick={onClick}
               disabled={disabled || isTranscribing}
               aria-label={label}
               aria-pressed={isRecording}
               data-testid="dictation-button"
               className={clsx(
                  "h-8 w-8 p-0 shrink-0 rounded-lg flex items-center justify-center focus:outline-none transition-colors duration-200",
                  "disabled:opacity-50",
                  isRecording
                     ? "bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-950 dark:text-red-400 dark:hover:bg-red-900"
                     : "bg-transparent text-800 hover:bg-200"
               )}
            >
               {isTranscribing ? (
                  <Icon icon="spinner" type={ICON_TYPE.LIGHT} className="animate-spin" />
               ) : (
                  <Icon icon="microphone" type={ICON_TYPE.LIGHT} />
               )}
            </button>
         </TooltipTrigger>
         <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>
   );
}
